import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import {
  createStoreCoupon,
  deleteStoreCoupon,
  getStoreCoupons,
  updateStoreCoupon,
} from "../couponsService";
import { toErrorText } from "./storeSalesDashboardHelpers";

const EMPTY_FORM = {
  code: "",
  name: "",
  description: "",
  discount_type: "percentage",
  discount_level: "value",
  discount_value: "",
  minimum_order_value: "",
  max_discount_value: "",
  max_uses_per_customer: "",
  ends_at: "",
  applicable_status: "all",
  buyer_status: "all",
  public: false,
  active: true,
};

const toDatetimeLocal = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocal = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const StoreSalesDashboardCouponsSection = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getStoreCoupons();
      setCoupons(Array.isArray(response.coupons) ? response.coupons : []);
    } catch (err) {
      setError(toErrorText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (coupon) => {
    setEditing(coupon);
    setForm({
      name: coupon.name || "",
      description: coupon.description || "",
      discount_type: coupon.discount_type || "percentage",
      discount_level: coupon.discount_level || "value",
      discount_value: coupon.discount_value != null ? String(coupon.discount_value) : "",
      minimum_order_value:
        coupon.minimum_order_value != null ? String(coupon.minimum_order_value) : "",
      max_discount_value:
        coupon.discount_type === "absolute" || coupon.max_discount_value == null
          ? ""
          : String(coupon.max_discount_value),
      max_uses_per_customer:
        coupon.max_uses_per_customer != null ? String(coupon.max_uses_per_customer) : "",
      ends_at: toDatetimeLocal(coupon.ends_at),
      applicable_status: coupon.applicable_status || "all",
      buyer_status: coupon.buyer_status || "all",
      public: coupon.public === true,
      active: coupon.active !== false,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "discount_type" && value === "absolute") {
        next.max_discount_value = "";
      }
      return next;
    });
  };

  const buildPayload = () => ({
    name: form.name.trim() || null,
    description: form.description.trim() || null,
    discount_type: form.discount_type,
    discount_level: form.discount_level,
    discount_value: Number(form.discount_value),
    minimum_order_value: form.minimum_order_value === "" ? null : Number(form.minimum_order_value),
    max_discount_value:
      form.discount_type === "percentage" && form.max_discount_value !== ""
        ? Number(form.max_discount_value)
        : null,
    max_uses_per_customer:
      form.max_uses_per_customer === "" ? null : Number(form.max_uses_per_customer),
    ends_at: fromDatetimeLocal(form.ends_at),
    applicable_status: form.applicable_status,
    buyer_status: form.buyer_status,
    public: form.public === true,
    active: form.active !== false,
  });

  const formErrors = useMemo(() => {
    const discountValue = Number(form.discount_value);
    if (form.discount_value === "" || !Number.isFinite(discountValue) || discountValue <= 0) {
      return { discount_value: "Required" };
    }
    return {};
  }, [form.discount_value]);

  const canSave = Object.keys(formErrors).length === 0;
  const valueLabel =
    form.discount_type === "percentage" ? "Discount (%)" : "Discount (₹)";

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      if (editing) {
        await updateStoreCoupon(editing.id, payload);
      } else {
        const trimmedCode = form.code.trim();
        if (trimmedCode !== "") {
          payload.code = trimmedCode;
        }
        await createStoreCoupon(payload);
      }
      setModalOpen(false);
      await loadCoupons();
    } catch (err) {
      setError(toErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Deactivate or delete coupon ${coupon.code}?`)) return;
    setError(null);
    try {
      await deleteStoreCoupon(coupon.id);
      await loadCoupons();
    } catch (err) {
      setError(toErrorText(err));
    }
  };

  return (
    <>
      <div className="d-flex justify-content-end mb-3">
        <Button color="primary" size="sm" onClick={openCreate}>
          New coupon
        </Button>
      </div>

      {error ? (
        <Alert color="danger" fade={false} className="mb-3">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="py-4 text-center text-muted">
          <Spinner className="me-2" />
          Loading…
        </div>
      ) : (
        <Table responsive bordered hover size="sm" className="mb-0 store-sales-table bg-white">
          <thead className="store-sales-table__head">
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Type</th>
              <th>Level</th>
              <th>Value</th>
              <th>Order type</th>
              <th>Buyer</th>
              <th>Max uses</th>
              <th>Public</th>
              <th>Active</th>
              <th>Ends</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-muted text-center">
                  No coupons yet.
                </td>
              </tr>
            ) : (
              coupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td className="fw-semibold">{coupon.code}</td>
                  <td>{coupon.name}</td>
                  <td>{coupon.discount_type}</td>
                  <td>{coupon.discount_level}</td>
                  <td>{coupon.discount_value}</td>
                  <td>
                    {coupon.applicable_status === "sample"
                      ? "Sample"
                      : coupon.applicable_status === "non_sample"
                      ? "Non-sample"
                      : "All"}
                  </td>
                  <td className="text-capitalize">{coupon.buyer_status || "all"}</td>
                  <td>{coupon.max_uses_per_customer != null ? coupon.max_uses_per_customer : "—"}</td>
                  <td>
                    {coupon.public ? (
                      <Badge color="info">Public</Badge>
                    ) : (
                      <Badge color="secondary">Private</Badge>
                    )}
                  </td>
                  <td>
                    {coupon.active ? (
                      <Badge color="success">Active</Badge>
                    ) : (
                      <Badge color="light">Inactive</Badge>
                    )}
                  </td>
                  <td>{coupon.ends_at ? new Date(coupon.ends_at).toLocaleString() : "—"}</td>
                  <td className="text-nowrap">
                    <Button size="sm" color="link" onClick={() => openEdit(coupon)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      color="link"
                      className="text-danger"
                      onClick={() => handleDelete(coupon)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
        <ModalHeader toggle={closeModal}>
          {editing ? `Edit ${editing.code}` : "New coupon"}
        </ModalHeader>
        <ModalBody>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Code</label>
              {editing ? (
                <Input value={editing.code} readOnly disabled />
              ) : (
                <>
                  <Input
                    placeholder="Leave blank to auto-generate"
                    value={form.code}
                    onChange={(e) => handleField("code", e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <div className="form-text">
                    Optional. If empty, a unique code is generated on save.
                  </div>
                </>
              )}
            </div>
            <div className="col-12">
              <label className="form-label">Name</label>
              <Input
                placeholder="Optional"
                value={form.name}
                onChange={(e) => handleField("name", e.target.value)}
              />
            </div>
            <div className="col-12">
              <label className="form-label">Description</label>
              <Input
                type="textarea"
                rows="2"
                placeholder="Optional"
                value={form.description}
                onChange={(e) => handleField("description", e.target.value)}
              />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Applies to</label>
              <Input
                type="select"
                value={form.discount_level}
                onChange={(e) => handleField("discount_level", e.target.value)}
              >
                <option value="value">Order total</option>
                <option value="delivery">Delivery</option>
              </Input>
            </div>
            <div className="col-sm-6">
              <label className="form-label">Type</label>
              <Input
                type="select"
                value={form.discount_type}
                onChange={(e) => handleField("discount_type", e.target.value)}
              >
                <option value="percentage">Percentage</option>
                <option value="absolute">Fixed amount</option>
              </Input>
            </div>
            <div className="col-12">
              <label className="form-label">{valueLabel}</label>
              <Input
                type="number"
                min="0"
                value={form.discount_value}
                invalid={Boolean(formErrors.discount_value)}
                onChange={(e) => handleField("discount_value", e.target.value)}
              />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Min order (₹)</label>
              <Input
                type="number"
                min="0"
                placeholder="Optional"
                value={form.minimum_order_value}
                onChange={(e) => handleField("minimum_order_value", e.target.value)}
              />
            </div>
            {form.discount_type === "percentage" ? (
              <div className="col-sm-6">
                <label className="form-label">Max discount (₹)</label>
                <Input
                  type="number"
                  min="0"
                  placeholder="Optional"
                  value={form.max_discount_value}
                  onChange={(e) => handleField("max_discount_value", e.target.value)}
                />
              </div>
            ) : null}
            <div className="col-sm-6">
              <label className="form-label">Valid until</label>
              <Input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => handleField("ends_at", e.target.value)}
              />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Max uses per buyer</label>
              <Input
                type="number"
                min="1"
                placeholder="Optional"
                value={form.max_uses_per_customer}
                onChange={(e) => handleField("max_uses_per_customer", e.target.value)}
              />
            </div>
            <div className="col-sm-6">
              <label className="form-label">Order type</label>
              <Input
                type="select"
                value={form.applicable_status}
                onChange={(e) => handleField("applicable_status", e.target.value)}
              >
                <option value="all">All</option>
                <option value="sample">Sample</option>
                <option value="non_sample">Non-sample</option>
              </Input>
            </div>
            <div className="col-sm-6">
              <label className="form-label">Buyer</label>
              <Input
                type="select"
                value={form.buyer_status}
                onChange={(e) => handleField("buyer_status", e.target.value)}
              >
                <option value="all">All</option>
                <option value="prospect">Prospect</option>
                <option value="customer">Customer</option>
              </Input>
            </div>
            <div className="col-12 d-flex gap-4">
              <div className="form-check">
                <Input
                  type="checkbox"
                  id="dashboard-coupon-active"
                  checked={form.active}
                  onChange={(e) => handleField("active", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="dashboard-coupon-active">
                  Active
                </label>
              </div>
              <div className="form-check">
                <Input
                  type="checkbox"
                  id="dashboard-coupon-public"
                  checked={form.public}
                  onChange={(e) => handleField("public", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="dashboard-coupon-public">
                  Public (auto-apply)
                </label>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? "Saving…" : editing ? "Save" : "Create"}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default StoreSalesDashboardCouponsSection;
